import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  ArrowsClockwise,
  Pulse,
  Scales,
  Warning,
} from "@phosphor-icons/react";

/**
 * /admin/issuing-reconciliation — drift history dashboard
 *
 * Surfaces the issuing_reconciliation_runs collection populated by
 * services/issuing_reconciliation.reconcile_issuing_balance(). Includes:
 *   • Last 30-day drift sparkline
 *   • Latest run summary card (Stripe balance vs ledger, drift, status)
 *   • Manual-trigger button (kicks off a reconciliation pass on demand)
 *   • Run-by-run audit table (most recent first)
 */

function Sparkline({ runs }) {
  // Render a minimal SVG sparkline of |drift_usd| over the last N runs.
  // Bars: green if below threshold, red if above. Aligned right-to-left
  // so the most recent run is on the right edge (matches reading order).
  if (!runs || runs.length === 0) {
    return <p className="text-xs text-[#4A5C50] mono italic">No data yet.</p>;
  }
  const ordered = [...runs].slice(0, 30).reverse();
  const maxAbs = Math.max(0.01, ...ordered.map((r) => Math.abs(r.drift_usd || 0)));
  const barW = 8;
  const gap = 4;
  const h = 60;
  const width = ordered.length * (barW + gap);

  return (
    <svg width={width} height={h + 20} className="overflow-visible" data-testid="reconciliation-sparkline">
      {ordered.map((r, i) => {
        const abs = Math.abs(r.drift_usd || 0);
        const hBar = Math.max(2, (abs / maxAbs) * h);
        const x = i * (barW + gap);
        const y = h - hBar;
        const aboveThreshold = abs > (r.threshold_usd || 0.01);
        const color = aboveThreshold ? "#B04E2F" : "#184A30";
        return (
          <g key={r.id || i}>
            <rect x={x} y={y} width={barW} height={hBar} fill={color} rx="2">
              <title>{`${new Date(r.checked_at).toLocaleString()}\nDrift: $${(r.drift_usd || 0).toFixed(2)}`}</title>
            </rect>
          </g>
        );
      })}
      <line x1="0" y1={h} x2={width} y2={h} stroke="#E5E0D8" strokeWidth="1" />
      <text x="0" y={h + 14} fontSize="9" fontFamily="monospace" fill="#4A5C50">
        ←oldest
      </text>
      <text x={width} y={h + 14} fontSize="9" fontFamily="monospace" fill="#4A5C50" textAnchor="end">
        newest→
      </text>
    </svg>
  );
}

function StatusPill({ run }) {
  if (!run.ok) {
    return (
      <span className="text-[10px] tracking-widest uppercase mono px-2 py-0.5 rounded-full bg-[#4A5C50]/10 text-[#4A5C50] border border-[#4A5C50]/20">
        Skipped
      </span>
    );
  }
  if (run.above_threshold) {
    return (
      <span className="text-[10px] tracking-widest uppercase mono px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
        Drift ⚠
      </span>
    );
  }
  return (
    <span className="text-[10px] tracking-widest uppercase mono px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
      In sync
    </span>
  );
}

export default function IssuingReconciliationAdmin() {
  const [runs, setRuns] = useState([]);
  const [lastAlert, setLastAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggerBusy, setTriggerBusy] = useState(false);
  const [triggerResult, setTriggerResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/issuing-reconciliation/runs?limit=60");
      setRuns(r.data.runs || []);
      setLastAlert(r.data.last_alert);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setTriggerBusy(true);
    setTriggerResult(null);
    try {
      const r = await api.post("/admin/issuing-reconciliation/run");
      setTriggerResult(r.data);
      await load();
    } catch (e) {
      setTriggerResult({ ok: false, error: e?.message || "Trigger failed" });
    } finally {
      setTriggerBusy(false);
    }
  };

  const latest = runs[0];
  const driftEvents = runs.filter((r) => r.above_threshold);

  return (
    <div className="min-h-screen bg-[#FBF9F6] text-[#112318]" data-testid="issuing-reconciliation-admin">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-[#4A5C50] hover:text-[#184A30]" data-testid="reconciliation-back">
          <ArrowLeft size={14} /> Back to Admin
        </Link>
        <header className="mt-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Compliance · Stripe Issuing</p>
          <h1 className="serif text-4xl mt-1 flex items-center gap-3">
            <Scales size={32} weight="duotone" className="text-[#184A30]" /> Issuing balance reconciliation
          </h1>
          <p className="text-[#4A5C50] mt-2 leading-relaxed max-w-2xl text-sm">
            Daily comparison of the Stripe Issuing balance vs the sum of
            remaining preload-balance on every <span className="mono">provider=stripe</span> card in our
            ledger. Drift &gt; threshold means either a missed{" "}
            <span className="mono">issuing_transaction.created</span> webhook
            or a Stripe-side authorization we didn't ledger — both are
            compliance fires.
          </p>
        </header>

        {/* Latest run hero card */}
        <section className="mt-6 rounded-2xl bg-[#112318] text-[#FBF9F6] p-6" data-testid="reconciliation-latest">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <p className="text-[10px] tracking-[0.3em] opacity-70 uppercase">Latest reconciliation</p>
              {latest ? (
                <p className="text-sm opacity-70 mono mt-1">{new Date(latest.checked_at).toLocaleString()}</p>
              ) : (
                <p className="text-sm opacity-70 mono mt-1">No runs yet.</p>
              )}
            </div>
            <Button
              size="sm"
              onClick={runNow}
              disabled={triggerBusy}
              className="rounded-xl bg-[#D96C4A] hover:bg-[#C75A3A] text-white text-xs"
              data-testid="reconciliation-run-now"
            >
              <ArrowsClockwise size={14} weight="bold" /> {triggerBusy ? "Running…" : "Run now"}
            </Button>
          </div>
          {latest && latest.ok && (
            <div className="grid sm:grid-cols-3 gap-4 mt-5">
              <div>
                <p className="text-[10px] tracking-widest opacity-60 uppercase">Stripe Issuing balance</p>
                <p className="mono text-2xl font-semibold mt-1" data-testid="reconciliation-stripe-balance">
                  ${(latest.stripe_balance_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest opacity-60 uppercase">MongoDB ledger</p>
                <p className="mono text-2xl font-semibold mt-1" data-testid="reconciliation-ledger-balance">
                  ${(latest.ledger_balance_usd ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-[10px] opacity-60 mono mt-0.5">{latest.cards_counted || 0} active cards</p>
              </div>
              <div>
                <p className="text-[10px] tracking-widest opacity-60 uppercase">Drift</p>
                <p
                  className={`mono text-2xl font-semibold mt-1 ${latest.above_threshold ? "text-[#D96C4A]" : "text-[#8BA888]"}`}
                  data-testid="reconciliation-drift"
                >
                  {latest.drift_usd >= 0 ? "+" : "−"}${Math.abs(latest.drift_usd || 0).toFixed(2)}
                </p>
                <div className="mt-1"><StatusPill run={latest} /></div>
              </div>
            </div>
          )}
          {latest && !latest.ok && (
            <div className="mt-4 p-3 bg-[#FBF9F6]/10 rounded-xl text-sm">
              <p className="opacity-90"><Warning size={14} weight="fill" className="inline" /> {latest.skipped_reason || "Run skipped"}</p>
              <p className="text-xs opacity-60 mt-1">Reconciliation is a no-op until <span className="mono">ISSUANCE_PROVIDER=stripe</span> + Stripe Issuing is funded.</p>
            </div>
          )}
        </section>

        {/* Trigger result inline feedback */}
        {triggerResult && (
          <section
            className={`mt-4 rounded-xl border p-4 text-sm ${
              triggerResult.ok
                ? triggerResult.above_threshold
                  ? "bg-rose-50 border-rose-200 text-rose-900"
                  : "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-[#FBF9F6] border-[#E5E0D8] text-[#4A5C50]"
            }`}
            data-testid="reconciliation-trigger-result"
          >
            <p className="mono text-xs">
              {triggerResult.ok
                ? triggerResult.above_threshold
                  ? `⚠ Manual run completed — DRIFT $${Math.abs(triggerResult.drift_usd || 0).toFixed(2)} detected.`
                  : `✓ Manual run completed — balances in sync.`
                : `Skipped: ${triggerResult.skipped_reason || triggerResult.error}`}
            </p>
          </section>
        )}

        {/* Sparkline */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Pulse size={20} weight="duotone" className="text-[#184A30]" />
              <h2 className="serif text-2xl">Drift over last 30 runs</h2>
            </div>
            <p className="text-xs text-[#4A5C50] mono">
              Red = above threshold · Green = in sync · Hover for value
            </p>
          </div>
          <Sparkline runs={runs} />
          {driftEvents.length > 0 && (
            <p className="text-xs text-[#B04E2F] mt-3 mono" data-testid="reconciliation-drift-count">
              ⚠ {driftEvents.length} run{driftEvents.length === 1 ? "" : "s"} exceeded threshold in this window
            </p>
          )}
          {lastAlert && (
            <p className="text-xs text-[#4A5C50] mt-2 mono">
              Last alert email sent: <span className="font-semibold">{new Date(lastAlert.alerted_at || lastAlert.checked_at).toLocaleString()}</span> · drift ${Math.abs(lastAlert.drift_usd || 0).toFixed(2)}
            </p>
          )}
        </section>

        {/* Run-by-run table */}
        <section className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white" data-testid="reconciliation-history-table">
          <div className="p-5 border-b border-[#E5E0D8]">
            <h2 className="serif text-2xl">Run history</h2>
            <p className="text-xs text-[#4A5C50]">Most recent first · 60 runs max</p>
          </div>
          {loading ? (
            <p className="p-5 text-sm text-[#4A5C50] mono">Loading…</p>
          ) : runs.length === 0 ? (
            <p className="p-5 text-sm text-[#4A5C50]">
              No reconciliation runs yet. They auto-start once{" "}
              <span className="mono">ISSUANCE_PROVIDER=stripe</span> is set, or trigger one manually above.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FBF9F6] text-[#4A5C50] text-[10px] tracking-widest uppercase">
                <tr>
                  <th className="text-left p-3">When</th>
                  <th className="text-right p-3">Stripe USD</th>
                  <th className="text-right p-3">Ledger USD</th>
                  <th className="text-right p-3">Drift</th>
                  <th className="text-right p-3">Cards</th>
                  <th className="text-right p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-t border-[#E5E0D8]" data-testid={`reconciliation-row-${r.id}`}>
                    <td className="p-3 mono text-xs">{new Date(r.checked_at).toLocaleString()}</td>
                    <td className="p-3 text-right mono">
                      {r.stripe_balance_usd !== null ? `$${r.stripe_balance_usd.toFixed(2)}` : "—"}
                    </td>
                    <td className="p-3 text-right mono">
                      {r.ledger_balance_usd !== null ? `$${r.ledger_balance_usd.toFixed(2)}` : "—"}
                    </td>
                    <td className={`p-3 text-right mono ${r.above_threshold ? "text-[#B04E2F] font-semibold" : ""}`}>
                      {r.drift_usd !== null
                        ? `${r.drift_usd >= 0 ? "+" : "−"}$${Math.abs(r.drift_usd).toFixed(2)}`
                        : "—"}
                    </td>
                    <td className="p-3 text-right mono">{r.cards_counted || 0}</td>
                    <td className="p-3 text-right"><StatusPill run={r} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <footer className="mt-10 text-xs text-[#4A5C50]">
          Thresholds + recipients configured via <span className="mono">ISSUING_RECONCILIATION_ALERT_USD</span> and{" "}
          <span className="mono">ISSUING_RECONCILIATION_NOTIFY_EMAIL</span> env vars.
          Full policy: <span className="mono">/app/memory/COMPLIANCE_PROGRAM.md</span>.
        </footer>
      </div>
    </div>
  );
}
